import { api } from '../api.js'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const id = (value) => {
  if (!uuid.test(value)) throw new Error('Invalid diagnostic identifier')
  return value
}
function base(pair) {
  if (![pair?.stationId, pair?.controllerId].every((value) => Number.isSafeInteger(value) && value > 0)) {
    throw new Error('Invalid diagnostic scope')
  }
  return `/platform/stations/${pair.stationId}/pts2-controllers/${pair.controllerId}/diagnostics`
}
export const diagnosticWindows = (pair, signal) => api(`${base(pair)}/windows`, { signal })
export const startDiagnosticWindow = (pair, requestId, signal) => api(`${base(pair)}/windows`, {
  method: 'POST', signal, body: JSON.stringify({ requestId: id(requestId), durationSeconds: 60 }),
})
export const stopDiagnosticWindow = (pair, windowId, signal) => api(`${base(pair)}/windows/${id(windowId)}/stop`, {
  method: 'POST', signal, body: '{}',
})
export const requestDiagnosticConfiguration = (pair, windowId, requestId, signal) => api(`${base(pair)}/windows/${id(windowId)}/configuration-request`, {
  method: 'POST', signal, body: JSON.stringify({ requestId: id(requestId) }),
})
export function diagnosticSamples(pair, { windowId = '', cursor = '', signal } = {}) {
  const query = new URLSearchParams({ limit: '20' })
  if (windowId) query.set('windowId', id(windowId))
  if (cursor) query.set('cursor', cursor)
  return api(`${base(pair)}/samples?${query}`, { signal })
}
export const diagnosticSample = (pair, sampleId, signal) => api(`${base(pair)}/samples/${id(sampleId)}`, { signal })

const errors = new Set(['not_authenticated', 'password_change_required', 'diagnostics_invalid_request',
  'diagnostics_forbidden', 'diagnostics_not_found', 'diagnostics_session_unavailable', 'diagnostics_window_active',
  'diagnostics_request_conflict', 'diagnostics_capacity_exceeded', 'diagnostics_disabled', 'diagnostics_unavailable'])
export function diagnosticErrorKey(failure) {
  if ([401, 403, 404, 428].includes(failure?.status)) return 'pts2DiagDenied'
  return errors.has(failure?.code) ? `pts2DiagError_${failure.code}` : 'pts2DiagError_diagnostics_unavailable'
}

// Translate only known codes. Unknown values never become presentation text.
export const diagnosticCodes = Object.freeze({
  availability: ['READY', 'DISABLED', 'UNAVAILABLE'],
  window: ['ACTIVE', 'STOPPED', 'EXPIRED', 'REVOKED', 'SESSION_CHANGED', 'RESTARTED', 'UNAVAILABLE', 'QUOTA_REACHED'],
  configuration: ['QUEUED', 'SENDING', 'SENT', 'RECEIVED', 'CANCELLED', 'EXPIRED', 'FAILED'],
  capture: ['PENDING', 'STORED', 'UNAVAILABLE', 'PURGED', 'KEY_UNAVAILABLE', 'CORRUPT'],
  completion: ['COMPLETE', 'INCOMPLETE', 'TRUNCATED', 'UNAVAILABLE'],
  receipt: ['REJECTED', 'RECEIVED', 'COMMITTED', 'ACK_SENT', 'PROCESSED', 'UNKNOWN'],
  stage: ['NONE', 'TRANSPORT', 'UTF8', 'JSON', 'JSONPTS', 'STORAGE', 'INCOMPLETE', 'UNKNOWN'],
  code: ['NONE', 'INVALID_FRAME', 'UNSUPPORTED_BINARY', 'INVALID_UTF8', 'INVALID_JSON', 'INVALID_JSONPTS',
    'MESSAGE_LIMIT', 'DISCONNECTED', 'ASSEMBLY_TIMEOUT', 'CAPTURE_BYTE_LIMIT', 'CAPTURE_FRAME_LIMIT',
    'QUEUE_FULL', 'STORAGE_LIMIT', 'STORE_FAILED', 'KEY_UNAVAILABLE', 'ENCRYPTION_FAILED', 'REVOKED', 'UNKNOWN'],
  reason: ['NONE', 'UNAVAILABLE', 'RAM_LIMIT', 'BYTE_LIMIT', 'FRAME_LIMIT', 'ASSEMBLY_TIMEOUT', 'WINDOW_EXPIRED',
    'SESSION_CHANGED', 'REVOKED', 'QUEUE_FULL', 'STORE_FAILED', 'PURGED', 'KEY_UNAVAILABLE', 'CORRUPT',
    'STORAGE_LIMIT', 'ENCRYPTION_FAILED', 'RESTARTED'],
  opcode: ['TEXT', 'BINARY', 'CONTINUATION', 'PING', 'PONG', 'CLOSE'],
})
export function diagnosticLabel(t, group, value) {
  return diagnosticCodes[group]?.includes(value) ? t(`pts2Diag_${group}_${value}`) : t('pts2DiagUnknown')
}
const packetTypes = new Set(['UploadStatus', 'UploadConfiguration', 'UploadPumpTransaction', 'UploadTankMeasurement',
  'UploadInTankDelivery', 'UploadAlertRecord'])
export const diagnosticPacketType = (value) => packetTypes.has(value) ? value : null
